import { ReactNode, useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';

interface Props {
  /** Number of secondary filters currently active (shows as a small badge on the button). */
  activeCount?: number;
  /** Pill markup for each filter group goes here. */
  children: ReactNode;
  /** Optional override label. Defaults to "Filter". */
  label?: string;
}

export const FilterPopover = ({ activeCount = 0, children, label = 'Filter' }: Props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className={`filter-pill ${activeCount > 0 ? 'active' : ''}`}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="filter" size={12} />
        {label}
        {activeCount > 0 && (
          <span
            style={{
              marginLeft: 4,
              background: 'var(--ink)',
              color: 'var(--bg)',
              borderRadius: 8,
              padding: '0 6px',
              fontSize: 10,
              fontWeight: 600,
            }}
          >
            {activeCount}
          </span>
        )}
        <Icon name="chevronDown" size={11} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 10,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--shadow-md)',
            padding: 12,
            minWidth: 240,
            maxWidth: 360,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

interface GroupProps {
  label: string;
  children: ReactNode;
}

export const FilterGroup = ({ label, children }: GroupProps) => (
  <div>
    <div
      style={{
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'var(--ink-4)',
        fontWeight: 600,
        marginBottom: 6,
      }}
    >
      {label}
    </div>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{children}</div>
  </div>
);

export default FilterPopover;
